<template>
    <v-container class="pa-5" fluid>
        <page-title>Moje raporty / Raporty szczegółowe</page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="userDetailedReports"
                        :loading="userDetailedReportsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        name="userDetailedReportsData"
                        @pagination="updatePagination"
                    >
                        <template #item.objectNumber="{item}">
                            {{ item.objectNumber }}
                            <v-tooltip 
                                v-if="item.productName"
                                :open-delay="tooltipDelay"
                                top
                            >
                                <template #activator="{on}">
                                    <v-chip
                                        class="pa-1 ml-1"
                                        color="transparent"
                                        v-on="on"
                                    >
                                        <v-icon color="green">
                                            mdi-comment-text-outline
                                        </v-icon>
                                    </v-chip>
                                </template>
                                <span>{{ `Nazwa produktu: ${item.productName}` }}</span>
                            </v-tooltip>
                        </template>
                        <template #item.userName="{item}">
                            {{ item.userName }}
                        </template>
                        <template #item.machineSymbol="{item}">
                            {{ item.machineSymbol }}
                        </template>
                        <template #item.processName="{item}">
                            {{ item.processName }}
                        </template>
                        <template #item.plateNumber="{item}">
                            {{ item.plateNumberRead }}
                        </template>
                        <template #item.reportAmount="{item}">
                            {{ parseEmptyValue(item.reportAmount) }}
                        </template>
                        <template #item.statusId="{item}">
                            {{ statusName(item.statusId) }}
                        </template>
                        <template #item.machineStuffAmount="{item}">
                            {{ item.machineStuffAmount }}
                        </template>
                        <template #item.startDate="{item}">
                            {{ formatDate(item.start) }}
                        </template>
                        <template #item.finishDate="{item}">
                            {{ formatDate(item.finish) }}
                        </template>
                        <template #item.duration="{item}">
                            {{ parseDuration(item.duration) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Szczegóły raportu"
                                            v-on="on"
                                            @click="openReport(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Szczegóły raportu</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'DetailedReports',
    components: {
        PageTitle
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse,
        filters,
        pagination
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('reports/getUserDetailedReports', {
            reportShiftSummary_id: to.params.id,
            paginate: '?limit=10'
        })
            .then(response => {
                next(vm => {
                    vm.handlePaginationResponse(response)
                    vm.reportShiftSummaryId = to.params.id
                })
            })
            .catch(error => {
                errorHandler(error)
                next(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('reports/clearDetailedReportsFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            reportShiftSummaryId: null,
            headers: [
                {
                    text: 'Numer',
                    value: 'objectNumber',
                    sortable: false,
                },
                {
                    text: 'Użytkownik',
                    value: 'userName',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Maszyna',
                    value: 'machineSymbol',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Proces',
                    value: 'processName',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Montaż',
                    value: 'plateNumber',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Nakład wykonany',
                    value: 'reportAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Ilość osób',
                    value: 'machineStuffAmount',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Status',
                    value: 'statusId',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'startDate',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data zakończenia',
                    value: 'finishDate',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Czas trwania',
                    value: 'duration',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        userDetailedReports() {
            return this.$store.state.reports.reports.userDetailedReports
        },
        userDetailedReportsLoading() {
            return this.$store.state.reports.reports.userDetailedReportsLoading
        },
        statusNames() {
            return this.$store.state.reports.reports.statusNames
        },
        savedFilters() {
            return this.$store.state.reports.reports.detailedReportsFilters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    mounted() {
        this.$store.dispatch('reports/getStatusNames')
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getUserDetailedReports() {
            this.$store.dispatch('reports/getUserDetailedReports', {
                reportShiftSummary_id: this.reportShiftSummaryId,
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openReport(report) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()

            this.$router.push({
                name: 'reportsUserReportsShiftSummariesDetailedReportsReport',
                params: {
                    reportShiftSummaryId: this.reportShiftSummaryId,
                    id: report.id,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        statusName(statusId) {
            return this.statusNames ? this.statusNames.find(item => item.id == statusId).name : statusId
        },
        close() {
            this.$router.push({ name: 'reportsUserReportsShiftSummaries' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.$store.dispatch('reports/setDetailedReportsFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.resetPagination()
            this.getUserDetailedReports()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && this.reportShiftSummaryId) {
                this.getUserDetailedReports()
            }
            this.clearPaginationPending()
        }
    }
}

</script>